import React, { FormEvent, MouseEvent, ReactNode, useCallback } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import classnames from "classnames";

import "../../../css/buttons/toggle_button.css";
import Tooltipable from "../shared/tooltipable";


export type Props<Value = any> = {
  /** If `true`, the button will be disabled. */
  disabled?: boolean
  /**
   * If `true`, the  keyboard focus ripple will be disabled.
   * `disableRipple` must also be true.
   */
  disableFocusRipple?: boolean
  /** If `true`, the ripple effect will be disabled. */
  disableRipple?: boolean
  onChange?: (event: FormEvent<HTMLButtonElement>, value: Value) => void
  onClick?: (event: MouseEvent<HTMLButtonElement>, value: Value) => void
  /** If `true`, the button will be rendered in an active state. */
  selected?: boolean
  size?: "small" | "medium" | "large"
  /**
   * The value to associate with the button when selected in a
   * ToggleButtonGroup.
   */
  value: Value
  className?: string
  children: ReactNode,
  tooltipTitle?: string
}

const ToggleButton = React.forwardRef<HTMLButtonElement, Props>(function ToggleButton(props, ref) {
  const {
    children,
    className,
    disabled = false,
    disableFocusRipple = false,
    disableRipple = false,
    onChange,
    onClick,
    selected,
    tooltipTitle,
    value,
    ...other
  } = props;

  const handleChange = useCallback(event => {
    if (onClick) {
      onClick(event, value);
      if (event.isDefaultPrevented()) {
        return;
      }
    }

    if (onChange) {
      onChange(event, value);
    }
  }, [onChange, onClick, value]);

  const button = <ButtonBase
    className={classnames(
      "ToggleButton",
      {
        "ToggleButton-disabled": disabled,
        "ToggleButton-selected": selected,
      },
      className,
    )}
    disabled={disabled}
    focusRipple={!disableFocusRipple}
    ref={ref}
    onClick={handleChange}
    value={value}
    disableRipple={disableRipple}
    aria-pressed={selected}
    {...other}
  >
    <span className="ToggleButton-label">{children}</span>
  </ButtonBase>;

  if (tooltipTitle == null) {
    return button;
  } else {
    return <Tooltipable title={tooltipTitle}>{button}</Tooltipable>;
  }
});

export default React.memo(ToggleButton);
